import { ExecutionResult } from "graphql";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate, useLocation } from "react-router";

import {
  useUpdateClaimMutation,
  UpdateClaimMutation,
} from "src/api/claims/updateClaim.generated";
import { Route } from "src/shared/constants/route";
import { useEventContext, useUserContext } from "src/shared/contexts";
import { useRsvpFlow } from "src/shared/hooks";
import {
  getFieldAnswer,
  parseState,
  convertToFileAnswers,
} from "src/shared/utils/hackerapi";
import { getRsvpDeadline } from "src/views/hacker/rsvp/constants";

import {
  createRsvpAutosaveController,
  RsvpAutosaveController,
  RsvpAutosaveStatus,
  RsvpAutosaveTask,
} from "./autosave";
import { DEFAULT_DATA } from "./defaults";
import {
  GetHackerQuery,
  useGetHackerLazyQuery,
} from "./graphql/getHacker.generated";
import {
  UpdateHackerStageMutation,
  useUpdateHackerStageMutation,
} from "./graphql/updateHackerStage.generated";
import { HackerStage, TAnswers, Field } from "./types";

const FIELDS = Object.values(Field);

// TODO: Improve this
const parseClaim = (data: GetHackerQuery["claims"][0] | undefined) =>
  FIELDS.reduce((obj, field) => {
    obj[field] = getFieldAnswer(data?.fields, field) ?? DEFAULT_DATA[field];
    return obj;
  }, {}) as TAnswers;

export type THackerContextState = {
  isFetchingData: boolean;
  claimFound: boolean | null;
  isUpdatingData: boolean;
  isUpdatingStage: boolean;
  isLoading: boolean;
  isReadOnly: boolean;
  error: string | undefined;
  stage: HackerStage | undefined;
  claimData: TAnswers;
  autosaveStatus: RsvpAutosaveStatus;
  hasUnsavedAutosave: boolean;
  queueAutosave: (key: string, task: RsvpAutosaveTask) => void;
  cancelAutosave: (key: string) => void;
  waitForAutosave: () => Promise<void>;
  queueClaimAutosave: (field: Field, value: TAnswers[Field]) => void;
  updateStage: (
    newStage: HackerStage
  ) => Promise<ExecutionResult<UpdateHackerStageMutation>>;
  updateResponses: (
    updatedData?: Partial<TAnswers>
  ) => Promise<ExecutionResult<UpdateClaimMutation>>;
  navigateToRSVP: () => void;
  navigateNext: () => void;
  navigateBack: () => void;
  navigateToTravelForm: () => void;
  refetch: ReturnType<typeof useGetHackerLazyQuery>["1"]["refetch"];
  // reimbursementAmount: number;
};

const DEFAULT_STATE: THackerContextState = {
  isFetchingData: false,
  claimFound: null,
  isUpdatingData: false,
  isUpdatingStage: false,
  isLoading: false,
  isReadOnly: true,
  error: undefined,
  stage: undefined,
  claimData: DEFAULT_DATA,
  autosaveStatus: RsvpAutosaveStatus.SAVED,
  hasUnsavedAutosave: false,
  queueAutosave: () => {},
  cancelAutosave: () => {},
  waitForAutosave: () => Promise.resolve(),
  queueClaimAutosave: () => {},
  updateStage: () => Promise.reject("No parent HackerContextProvider found"),
  updateResponses: () =>
    Promise.reject("No parent HackerContextProvider found"),
  navigateToRSVP: () => {},
  navigateNext: () => {},
  navigateBack: () => {},
  navigateToTravelForm: () => {},
  refetch: () => Promise.reject("No parent HackerContextProvider found"),
};

const HackerContext: React.Context<THackerContextState> =
  createContext(DEFAULT_STATE);

export const useHackerContext = () => useContext(HackerContext);

const removeFields = (state: TAnswers) => {
  Object.entries(state).forEach(([key]) => {
    if (key.includes("interests")) {
      delete state[key];
    }
  });
};

const convertFieldTypes = (state: TAnswers) => {
  if (state[Field.TRAVEL_RECEIPT]) {
    state[Field.TRAVEL_RECEIPT] = convertToFileAnswers(
      state[Field.TRAVEL_RECEIPT]
    );
  }

  if (state[Field.RESUME]) {
    state[Field.RESUME] = convertToFileAnswers(state[Field.RESUME]);
  }

  if (state[Field.WAIVER_CONSENT]) {
    state[Field.WAIVER_CONSENT] = convertToFileAnswers(
      state[Field.WAIVER_CONSENT]
    );
  }
};

export const HackerContextProvider: React.FC = ({ children }) => {
  const { id } = useUserContext();
  const { rsvp } = useEventContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { steps, isVolunteer } = useRsvpFlow();

  const [
    getHackerQuery,
    { loading: getLoading, data: getData, error: getError, refetch },
  ] = useGetHackerLazyQuery();

  // const [getTravelReimbursementAmountQuery, { data: getReimbursementData }] =
  //   useTravelReimbursementAmountLazyQuery();

  const [
    updateHacker,
    {
      loading: updateHackerLoading,
      data: updateHackerData,
      error: updateHackerError,
    },
  ] = useUpdateClaimMutation();

  const [autosaveHacker] = useUpdateClaimMutation({
    fetchPolicy: "no-cache",
  });

  const [autosaveStatus, setAutosaveStatus] = useState(
    RsvpAutosaveStatus.SAVED
  );
  const autosaveControllerRef = useRef<RsvpAutosaveController | null>(null);
  if (!autosaveControllerRef.current) {
    autosaveControllerRef.current = createRsvpAutosaveController({
      onStatusChange: setAutosaveStatus,
    });
  }
  const autosaveController = autosaveControllerRef.current;
  const pendingClaimChangesRef = useRef<Partial<TAnswers>>({});
  const autosavedClaimChangesRef = useRef<Partial<TAnswers>>({});

  const [
    updateHackerStage,
    {
      loading: updateStageLoading,
      data: updateStageData,
      error: updateStageError,
    },
  ] = useUpdateHackerStageMutation();

  // fetch responses from hapi (if user id is defined)
  useEffect(() => {
    if (id) getHackerQuery({ variables: { myId: id } });
  }, [id, getHackerQuery]);

  const claim = getData?.claims?.[0];
  const claimFound = getData
    ? getData.claims.length > 0
    : getError
    ? false
    : null;

  const stage = (updateStageData?.updateClaim?.stage.slug ??
    claim?.stage.slug) as THackerContextState["stage"];

  const claimData = {
    ...parseClaim(claim ?? updateHackerData?.updateClaim),
    [Field.RSVP_DEADLINE]: getRsvpDeadline(rsvp, new Date(), isVolunteer),
  };
  const claimDataRef = useRef(claimData);
  claimDataRef.current = claimData;
  const claimIdRef = useRef(claim?.id);
  claimIdRef.current = claim?.id;

  const isAutosaveRoute = steps.some((step) => step.path === location.pathname);
  const isAutosaveRouteRef = useRef(isAutosaveRoute);
  isAutosaveRouteRef.current = isAutosaveRoute;

  const queueAutosave = useCallback(
    (key: string, task: RsvpAutosaveTask) => {
      if (isAutosaveRouteRef.current) autosaveController.queue(key, task);
    },
    [autosaveController]
  );

  const cancelAutosave = useCallback(
    (key: string) => autosaveController.cancel(key),
    [autosaveController]
  );

  const waitForAutosave = useCallback(
    () => autosaveController.waitForActiveSave(),
    [autosaveController]
  );

  const queueClaimAutosave = useCallback(
    (field: Field, value: TAnswers[Field]) => {
      if (!isAutosaveRouteRef.current) return;

      pendingClaimChangesRef.current[field] = value;
      autosaveController.queue("claim", async () => {
        const id = claimIdRef.current;
        if (!id) throw new Error("Undefined claim id");

        const pendingSnapshot = { ...pendingClaimChangesRef.current };
        const state = {
          ...claimDataRef.current,
          ...autosavedClaimChangesRef.current,
          ...pendingSnapshot,
        };
        removeFields(state);
        convertFieldTypes(state);

        const result = await autosaveHacker({
          variables: {
            updatedData: {
              id,
              answers: parseState(state),
            },
          },
        });
        if (result.errors) throw new Error(result.errors[0]?.message);

        autosavedClaimChangesRef.current = {
          ...autosavedClaimChangesRef.current,
          ...pendingSnapshot,
        };
        Object.keys(pendingSnapshot).forEach((key) => {
          const fieldKey = key as Field;
          if (
            pendingClaimChangesRef.current[fieldKey] ===
            pendingSnapshot[fieldKey]
          ) {
            delete pendingClaimChangesRef.current[fieldKey];
          }
        });
      });
    },
    [autosaveController, autosaveHacker]
  );

  const updateStage = useCallback(
    async (newStage: HackerStage) => {
      const id = claim?.id;

      if (!id) return Promise.reject("Undefined claim id");

      return await updateHackerStage({
        variables: {
          id,
          newStage,
        },
      });
    },
    [claim, updateHackerStage]
  );

  const updateResponses = useCallback(
    async (updatedData: Partial<TAnswers> = {}) => {
      const id = claim?.id;

      if (!id) return Promise.reject("Undefined claim id");

      autosaveController.cancel("claim");
      await autosaveController.waitForActiveSave();

      const state = {
        ...claimData,
        ...autosavedClaimChangesRef.current,
        ...pendingClaimChangesRef.current,
        ...updatedData,
      };

      // make sure interests are not passed to update claim
      removeFields(state);

      convertFieldTypes(state);

      const result = await updateHacker({
        variables: {
          updatedData: {
            id,
            answers: parseState(state),
          },
        },
      });

      if (!result.errors) {
        autosavedClaimChangesRef.current = {
          ...autosavedClaimChangesRef.current,
          ...pendingClaimChangesRef.current,
          ...updatedData,
        };
        pendingClaimChangesRef.current = {};
      }

      return result;
    },
    [autosaveController, claim, updateHacker, claimData]
  );

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!autosaveController.hasUnsavedChanges()) return;
      event.preventDefault();
      event.returnValue = "";
    };
    const flushOnPageHide = () => {
      if (autosaveController.hasUnsavedChanges()) {
        void autosaveController.flush();
      }
    };
    const flushWhenHidden = () => {
      if (document.visibilityState === "hidden") flushOnPageHide();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("pagehide", flushOnPageHide);
    document.addEventListener("visibilitychange", flushWhenHidden);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("pagehide", flushOnPageHide);
      document.removeEventListener("visibilitychange", flushWhenHidden);
      autosaveController.destroy();
    };
  }, [autosaveController]);

  const navigateToRSVP = () => navigate(Route.HACKER_RSVP);

  const navigateNext = () => {
    if (location.pathname === Route.HACKER_TRAVEL_DETAILS) {
      navigateToRSVP();
      window.location.reload();
      return;
    }

    const currentIndex = steps.findIndex(
      (step) => step.path === location.pathname
    );
    const nextStep = currentIndex === -1 ? steps[0] : steps[currentIndex + 1];
    navigate(nextStep ? nextStep.path : Route.HACKER_RSVP);
    window.scrollTo(0, 0);
  };

  const navigateBack = () => {
    const currentIndex = steps.findIndex(
      (step) => step.path === location.pathname
    );
    const previousStep = currentIndex > 0 ? steps[currentIndex - 1] : undefined;
    navigate(previousStep ? previousStep.path : Route.HACKER_RSVP);
    window.scrollTo(0, 0);
  };

  const navigateToTravelForm = () => {
    navigate(Route.HACKER_TRAVEL_DETAILS);
    window.scrollTo(0, 0);
  };

  // A completed empty/error response is a recovery state, not ongoing loading.
  const isFetchingData = getLoading || claimFound === null;
  const isUpdatingData = updateHackerLoading && !updateHackerData;
  const isUpdatingStage = updateStageLoading && !updateStageData;

  /**
   * Build state
   */
  const hackerState: THackerContextState = {
    isFetchingData: isFetchingData,
    claimFound,
    isUpdatingData: isUpdatingData,
    isUpdatingStage: isUpdatingStage,
    isLoading: isFetchingData || isUpdatingData || isUpdatingStage,
    isReadOnly:
      !!stage &&
      [HackerStage.CHECKED_IN, HackerStage.WITHDRAWN].includes(stage),
    error:
      getError?.message ??
      updateHackerError?.message ??
      updateStageError?.message ??
      undefined,
    stage,
    claimData,
    autosaveStatus,
    hasUnsavedAutosave: autosaveStatus !== RsvpAutosaveStatus.SAVED,
    queueAutosave,
    cancelAutosave,
    waitForAutosave,
    queueClaimAutosave,
    updateStage,
    updateResponses,
    navigateToRSVP,
    navigateNext,
    navigateBack,
    navigateToTravelForm,
    refetch,
  };

  return (
    <HackerContext.Provider value={hackerState}>
      {children}
    </HackerContext.Provider>
  );
};
